import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Typography,
} from "@mui/material";
import { fetchData } from "../../api/client";
import { usePageContext } from "../../context/PageContext";
import AddClientForm from "../../components/AddClientForm";
import ClientFormPageChrome from "../../components/clients/ClientFormPageChrome";
import ClientFormActionsBar from "../../components/clients/ClientFormActionsBar";
import DuplicateClientDialog from "../../components/clients/DuplicateClientDialog";
import { clientFormPageShellSx, clientFormScrollSx } from "./clientFormPageLayout";
import { getClientCommercialName, getRazaoSocial, isPessoaJuridica } from "../../utils/clientName";

const tipoNumToStr = (v) => (v === 1 ? "Pessoa Jurídica" : v === 0 ? "Pessoa Física" : "");

export default function ClientEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentPage } = usePageContext();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveState, setSaveState] = useState({
    disableSave: true,
    isSubmitting: false,
    isDirty: false,
    hint: "",
  });
  const [discardOpen, setDiscardOpen] = useState(false);
  const [dupDialog, setDupDialog] = useState({
    open: false,
    duplicates: [],
    attemptedPayload: null,
    secondaryClientId: null,
  });

  const handleSaveStateChange = useCallback((next) => {
    setSaveState((prev) => ({ ...prev, ...next }));
  }, []);

  const loadClient = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setLoadError(null);
      const resp = await fetchData(`/clients/${id}`, "GET");
      const fullClient = (resp && (resp.data || resp)) || null;
      if (!fullClient?.id) {
        setLoadError("Cliente não encontrado.");
        setClient(null);
        return;
      }
      setClient(fullClient);
    } catch (e) {
      setLoadError(e?.message || "Não foi possível carregar o cadastro do cliente.");
      setClient(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  const displayName = useMemo(() => {
    if (!client) return "";
    return getClientCommercialName(client) || client.apelido || "";
  }, [client]);

  const displaySubtitle = useMemo(() => {
    if (!client || !isPessoaJuridica(client)) return null;
    const razao = getRazaoSocial(client);
    const commercial = getClientCommercialName(client);
    if (razao && razao !== commercial) return razao;
    return null;
  }, [client]);

  const tipoLabel = useMemo(() => {
    if (!client) return "";
    return tipoNumToStr(Number(client.tipo));
  }, [client]);

  const statusLabel = useMemo(() => {
    if (!client) return "";
    const s = client.status;
    if (s === 0 || s === "0" || String(s).toLowerCase() === "inativo") return "Inativo";
    return "Ativo";
  }, [client]);

  const navigateBack = () => {
    navigate(`/clients/${id}`, { replace: false });
  };

  const handleCancel = () => {
    if (saveState.isDirty) {
      setDiscardOpen(true);
      return;
    }
    navigateBack();
  };

  const handleSubmit = async (payload) => {
    try {
      setSaving(true);
      setSaveError(null);
      const method = payload?.id ? "PUT" : "POST";
      const endpoint = payload?.id ? `/clients/${payload.id}` : "/clients";
      await fetchData(endpoint, method, payload);
      navigate(`/clients/${payload.id || id}?updated=1`, { replace: true });
    } catch (e) {
      if (e?.code === "errors.clients.duplicate") {
        setDupDialog({
          open: true,
          duplicates: e?.data?.duplicates || [],
          attemptedPayload: payload || null,
          secondaryClientId: payload?.id ? Number(payload.id) : null,
        });
        return;
      }
      setSaveError(e?.message || "Erro ao salvar o cliente.");
    } finally {
      setSaving(false);
    }
  };

  const formBusy = saving || saveState.isSubmitting;

  const statusText = useMemo(() => {
    if (formBusy) return null;
    if (saveState.validationErrorCount > 0) {
      return `${saveState.validationErrorCount} pendência${saveState.validationErrorCount > 1 ? "s" : ""} no formulário`;
    }
    if (saveState.isDirty) return "Alterações não salvas";
    return "Cadastro em dia";
  }, [formBusy, saveState.validationErrorCount, saveState.isDirty]);

  if (loading) {
    return (
      <Box sx={clientFormPageShellSx}>
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Skeleton variant="text" width={120} height={28} />
          <Skeleton variant="text" width="55%" height={40} sx={{ mt: 1 }} />
          <Skeleton variant="text" width="40%" height={24} />
        </Box>
        <Box sx={{ flex: 1 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={140} sx={{ mb: 2, borderRadius: 2.5 }} />
          ))}
        </Box>
      </Box>
    );
  }

  if (loadError || !client) {
    return (
      <Box sx={clientFormPageShellSx}>
        <Typography color="error" variant="h6" gutterBottom>
          {loadError || "Cliente não encontrado."}
        </Typography>
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={() => navigate(`/clients?page=${currentPage}`)}
          sx={{ cursor: "pointer", color: "primary.main", border: 0, bgcolor: "transparent", p: 0 }}
        >
          Voltar para a lista de clientes
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={clientFormPageShellSx}>
      <ClientFormPageChrome
        mode="edit"
        clientId={client.id}
        clientName={displayName}
        clientSubtitle={displaySubtitle}
        clientTypeLabel={tipoLabel}
        clientStatusLabel={statusLabel}
        listPage={currentPage}
        onCancel={handleCancel}
      />

      {saveError ? (
        <Alert severity="error" sx={{ mb: 1.5, flexShrink: 0 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      ) : null}

      <Box sx={clientFormScrollSx}>
        <AddClientForm
          client={client}
          isFetching={saving}
          onClose={handleCancel}
          onSubmit={handleSubmit}
          variant="page"
          formId="client-edit-form"
          hideFooterActions
          onSaveStateChange={handleSaveStateChange}
          onDuplicateDetected={({ duplicates, attemptedPayload }) => {
            setDupDialog({
              open: true,
              duplicates,
              attemptedPayload: attemptedPayload || null,
              secondaryClientId: client?.id ? Number(client.id) : null,
            });
          }}
        />
      </Box>

      <ClientFormActionsBar
        statusText={statusText}
        hintText={formBusy ? null : saveState.hint}
        isBusy={formBusy}
      >
        <Button variant="outlined" onClick={handleCancel} disabled={formBusy}>
          Cancelar
        </Button>
        <Button
          type="submit"
          form="client-edit-form"
          variant="contained"
          disabled={saveState.disableSave || formBusy}
        >
          {formBusy ? <CircularProgress size={20} /> : "Salvar alterações"}
        </Button>
      </ClientFormActionsBar>

      <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Descartar alterações?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você fez alterações que ainda não foram salvas. Se sair agora, elas serão perdidas.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDiscardOpen(false)}>Continuar editando</Button>
          <Button color="error" variant="contained" onClick={navigateBack}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      <DuplicateClientDialog
        open={dupDialog.open}
        duplicates={dupDialog.duplicates}
        attemptedPayload={dupDialog.attemptedPayload}
        secondaryClientId={dupDialog.secondaryClientId}
        secondaryLabel="cliente que você estava editando"
        onChangePhone={() => {
          setDupDialog((s) => ({ ...(s || {}), open: false }));
          setSaveError("Altere o telefone ou outro dado em conflito para continuar.");
        }}
        onClose={() => setDupDialog((s) => ({ ...(s || {}), open: false }))}
        onMerged={({ primaryId }) => {
          setDupDialog((s) => ({ ...(s || {}), open: false }));
          const targetId = primaryId || client.id;
          navigate(`/clients/${targetId}?merged=1`);
        }}
      />
    </Box>
  );
}
